import { Controller } from '@hotwired/stimulus'
import { debounce } from 'lodash'

export default class extends Controller {
  static targets = ['input', 'frame']
  static values = {
    submitUrl: String,
    frameId: String
  }

  declare readonly inputTargets: HTMLInputElement[]
  declare readonly frameTarget: HTMLDivElement
  declare readonly submitUrlValue: string
  declare readonly frameIdValue: string

  private debouncedSubmit: ReturnType<typeof debounce> | null = null
  private lastChangedInput: HTMLInputElement | null = null
  private changeHandler: (e: Event) => void
  private debouncedQuillChange: ReturnType<typeof debounce> | null = null
  private autoSubmitTimer: number | null = null
  private isFocused: boolean = false
  private readonly AUTO_SUBMIT_INTERVAL = 10000 // 10 seconds

  connect(): void {
    this.debouncedSubmit = debounce(this.submit.bind(this), 500)
    this.debouncedQuillChange = debounce(this.handleQuillChangeDebounced.bind(this), 1000)
    this.setupEventListeners()
  }

  disconnect(): void {
    if (this.debouncedSubmit) {
      this.debouncedSubmit.cancel()
    }
    if (this.debouncedQuillChange) {
      this.debouncedQuillChange.cancel()
    }
    this.stopAutoSubmitTimer()
    this.cleanupEventListeners()
  }

  private setupEventListeners(): void {
    this.changeHandler = this.handleChange.bind(this)

    this.element.addEventListener('quill:content-changed', this.handleQuillChange.bind(this))
    this.element.addEventListener('focusin', this.handleFocusIn.bind(this))
    this.element.addEventListener('focusout', this.handleFocusOut.bind(this))
    this.element.addEventListener('paste', this.handlePaste.bind(this))
  }

  private cleanupEventListeners(): void {
    this.element.removeEventListener('quill:content-changed', this.handleQuillChange.bind(this))
    this.element.removeEventListener('focusin', this.handleFocusIn.bind(this))
    this.element.removeEventListener('focusout', this.handleFocusOut.bind(this))
    this.element.removeEventListener('paste', this.handlePaste.bind(this))
  }

  private handleChange(event: Event): void {
    const target = event.target as HTMLElement
    if (target.matches('input, textarea, [contenteditable], .editor-content, .rich-text-editor')) {
      this.inputChanged(event)
    }
  }

  private handleQuillChange(event: CustomEvent): void {
    if (this.debouncedQuillChange) {
      this.debouncedQuillChange(event)
    }
  }

  private handleFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement
    if (target.matches('input, textarea, [contenteditable], .editor-content, .rich-text-editor, .ql-editor')) {
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        this.lastChangedInput = target as HTMLInputElement
      } else if (target.classList.contains('ql-editor')) {
        const quillContainer = target.closest('[data-controller*="input"]') as HTMLElement
        if (quillContainer) {
          const hiddenInput = quillContainer.querySelector('input[type="hidden"]') as HTMLInputElement
          if (hiddenInput) {
            this.lastChangedInput = hiddenInput
          }
        }
      }

      this.startAutoSubmit()
    }
  }

  private handleFocusOut(event: FocusEvent): void {
    const target = event.target as HTMLElement
    if (target.matches('input, textarea, [contenteditable], .editor-content, .rich-text-editor, .ql-editor')) {
      setTimeout(() => {
        this.stopAutoSubmit()
      }, 1000)
    }
  }

  private handlePaste(event: ClipboardEvent): void {
    console.log('handlePaste')
    const target = event.target as HTMLElement
    if (target.matches('input, textarea, [contenteditable], .editor-content, .rich-text-editor, .ql-editor')) {
      setTimeout(() => {
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          this.lastChangedInput = target as HTMLInputElement
        } else if (target.classList.contains('ql-editor')) {
          const quillContainer = target.closest('[data-controller*="input"]') as HTMLElement
          if (quillContainer) {
            const hiddenInput = quillContainer.querySelector('input[type="hidden"]') as HTMLInputElement
            if (hiddenInput) {
              this.lastChangedInput = hiddenInput
            }
          }
        }

        this.submit()
      }, 50)
    }
  }

  private handleQuillChangeDebounced(event: CustomEvent): void {
    this.inputChangedQuill(event)
  }

  public inputChanged(event: Event): void {
    const input = event.target as HTMLInputElement
    this.lastChangedInput = input

    if (this.debouncedSubmit) {
      this.debouncedSubmit()
    }
  }

  public inputChangedQuill(event: CustomEvent): void {
    const quillContainer = event.target as HTMLElement
    const input = quillContainer.querySelector('input[type="hidden"]') as HTMLInputElement

    if (input) {
      this.lastChangedInput = input

      if (this.debouncedSubmit) {
        this.debouncedSubmit()
      }
    }
  }

  private startAutoSubmit(): void {
    this.isFocused = true
    this.startAutoSubmitTimer()
  }

  private stopAutoSubmit(): void {
    this.isFocused = false
    this.stopAutoSubmitTimer()
  }

  private startAutoSubmitTimer(): void {
    this.stopAutoSubmitTimer()

    this.autoSubmitTimer = window.setTimeout(() => {
      if (this.isFocused && this.lastChangedInput) {
        this.submit()
        if (this.isFocused) {
          this.startAutoSubmitTimer()
        }
      }
    }, this.AUTO_SUBMIT_INTERVAL)
  }

  private stopAutoSubmitTimer(): void {
    if (this.autoSubmitTimer) {
      clearTimeout(this.autoSubmitTimer)
      this.autoSubmitTimer = null
    }
  }

  private async submit(): Promise<void> {
    if (!this.lastChangedInput) return

    const formData = new FormData()
    const noticeFormat = (this.element.closest('.field-container') as HTMLElement)?.dataset.format as string
    if (noticeFormat) {
      formData.append('notice_format', noticeFormat)
    }
    formData.append(this.lastChangedInput.name, this.lastChangedInput.value)
    formData.append('frame_id', this.frameIdValue)

    const url = this.submitUrlValue

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        body: formData,
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': this.getCsrfToken()
        }
      })

      const responseText = await response.text()

      if (responseText.includes('<turbo-stream')) {
        const parser = new DOMParser()
        const doc = parser.parseFromString(responseText, 'text/html')
        const turboStream = doc.querySelector('turbo-stream')

        // The below code is used to give feedback about the user input
        // It updates the UI with the server response: an error or success message
        if (turboStream) {
          const action = turboStream.getAttribute('action')
          const target = turboStream.getAttribute('target')
          const template = turboStream.querySelector('template')

          if (target && template) {
            const targetElement = document.getElementById(target)
            if (targetElement) {
              if (action === 'update') {
                targetElement.innerHTML = template.innerHTML
              } else if (action === 'append') {
                targetElement.insertAdjacentHTML('beforeend', template.innerHTML)
              } else if (action === 'replace') {
                targetElement.outerHTML = template.innerHTML
              }
            }
          }
        }
      }

    } catch (error) {
      console.error('Fetch error:', error)
    }
  }

  private getCsrfToken(): string {
    const metaTag = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement
    return metaTag ? metaTag.content : ""
  }
}
